import { Tag } from '@components';
import { useNavigateWithOfflineMode, useScreenSize } from '@hooks';
import { testApi } from '@store';
import { Flex, theme, Typography } from '@styles';
import { shape } from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { VerticalProgressBarWithElements } from 'src/components/VerticalProgressBarWithElements';

import { tagsProps } from '../../ProfilePage.layoutProps';

const OngoingModule = ({ ongoingModule }) => {
  const { navigate } = useNavigateWithOfflineMode();
  const { i18n } = useTranslation();
  const { isTabletSM } = useScreenSize();

  const { test } = testApi.useGetTestByIdQuery(
    { id: ongoingModule?.test_id },
    {
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data }) => ({
        test: data?.data,
      }),
    }
  );

  const memoTags = useMemo(
    () =>
      tagsProps
        ?.filter((el) => !!ongoingModule[el.key])
        ?.map((tag) => (
          <Flex display="block" mr="8px">
            <Tag
              content={
                tag.key === 'isTest'
                  ? i18n.t(tag.translationName)
                  : `${ongoingModule[tag.key]} ${i18n.t(tag.translationName)}`
              }
              variant={tag.keyTag}
            />
          </Flex>
        )),
    [ongoingModule, tagsProps]
  );

  const handleClickLesson = (lessonId) => () => {
    navigate(`/courses/${ongoingModule?.course_id}/${ongoingModule?.id}`, {
      state: { lessonId },
    });
  };

  const handleClickTest = () => {
    navigate(`/courses/${ongoingModule?.course_id}/${ongoingModule?.id}`, {
      state: { finalTest: true },
    });
  };

  return (
    <Flex
      backdropFilter="blur(17px)"
      bg={theme.colors.white[10]}
      flexDirection="column"
      justifyContent="center"
      minHeight={160}
      mr="3px"
    >
      <Flex
        borderColor={theme.colors.whiteLine[100]}
        borderStyle="solid"
        borderWidth={1}
        flexDirection={isTabletSM ? 'column' : 'row'}
        p={24}
      >
        <Flex
          alt="Module cover"
          backgroundImage={ongoingModule?.image}
          backgroundRepeat="no-repeat"
          backgroundSize="cover"
          height={isTabletSM ? 168 : 146}
          mr={24}
          width={isTabletSM ? '100%' : 270}
        />
        <Flex
          flexDirection="column"
          gap={isTabletSM ? 24 : 0}
          justifyContent="space-between"
          mb={isTabletSM ? 0 : 16}
          mt={isTabletSM ? 24 : 16}
        >
          <Flex flexDirection="column">
            <Typography
              color={theme.colors.white[100]}
              mb="8px"
              opacity="70%"
              variant="MS-regular-s16-lh25"
            >
              {`${i18n.t('ProfilePage.Module')} ${(ongoingModule?.order_number || 0) + 1}`}
            </Typography>
            <Typography
              color={theme.colors.white[100]}
              variant="MS-bold-s20-lh21"
            >
              {ongoingModule?.title}
            </Typography>
          </Flex>
          <Flex flexWrap="wrap">{memoTags.map((el) => el)}</Flex>
        </Flex>
      </Flex>
      <Flex
        borderColor={theme.colors.whiteLine[100]}
        borderStyle="solid"
        borderWidth={1}
        p={24}
      >
        <VerticalProgressBarWithElements
          currentLessonId={ongoingModule?.currentLessonId}
          elements={ongoingModule?.lessons}
          finalElement={
            ongoingModule?.test_id ? { ...test, is_passed: false } : null
          }
          handleClick={handleClickLesson}
          handleFinalElementClick={handleClickTest}
          isCurrent
          isProfileBar
        />
      </Flex>
    </Flex>
  );
};

OngoingModule.propTypes = {
  ongoingModule: shape.isRequired,
};

export default OngoingModule;
